import { useContext, useRef, useState, useEffect } from "react";
import { YearContext, AuthContext } from "../context/context";
import Headline from "../Components/Elements/Headline";
import StartMessage from "../Components/Elements/StartMessage";
import PageContentContainer from "../Components/ContentContainers/PageContentContainer";
import VisionBoard from "../Components/Elements/VisionBoard";
import { useLanguage } from "../context/LanguageContext";
import Toast from "../Components/Elements/Toast";
import { useVisionBoard } from "../context/VisionBoardContext";

const VisionBoardPage = () => {
  const { selectedYear } = useContext(YearContext);
  const { userData } = useContext(AuthContext);
  const { copy } = useLanguage();
  const visionBoardRef = useRef(null);
  const [isRendering, setIsRendering] = useState(false);
  const [toast, setToast] = useState({ message: "", type: "" });
  const [selectedRatio, setSelectedRatio] = useState(() => {
    // Initialize from localStorage if available, otherwise default to "video"
    return localStorage.getItem("visionBoardRatio") || "video";
  });

  // Save to localStorage when ratio changes
  useEffect(() => {
    localStorage.setItem("visionBoardRatio", selectedRatio);
  }, [selectedRatio]);

  const aspectRatios = {
    video: "aspect-video", // 16:9
    square: "aspect-[1/1]", // 1:1
    portrait: "aspect-[3/4]", // 3:4
    landscape: "aspect-[4/3]", // 4:3
  };

  const aspectRatioDimensions = {
    video: { width: 1920, height: 1080 }, // 16:9
    landscape: { width: 1600, height: 1200 }, // 4:3
    square: { width: 1200, height: 1200 }, // 1:1
    portrait: { width: 1200, height: 1600 }, // 3:4
  };

  const { layout, moodboardFilteredData } = useVisionBoard();

  const handleDownloadImage = async () => {
    const element = visionBoardRef.current;
    if (!element) {
      setToast({
        message: copy.vision_board.download.element_not_found,
        type: "error",
      });
      return;
    }

    setIsRendering(true);
    setToast({
      message: copy.vision_board.download.preparing,
      type: "info",
    });

    try {
      // Get dimensions based on selected ratio
      const { width: canvasWidth, height: canvasHeight } =
        aspectRatioDimensions[selectedRatio];

      // Define grid parameters
      const cols = 6;
      const rows = 4;
      const cellWidth = canvasWidth / cols;
      const cellHeight = canvasHeight / rows;

      // Create the canvas
      const canvas = document.createElement("canvas");
      canvas.width = canvasWidth;
      canvas.height = canvasHeight;
      const ctx = canvas.getContext("2d");
      ctx.fillStyle = "#ffffff";
      ctx.fillRect(0, 0, canvasWidth, canvasHeight);

      // Fetch all images and load them into Image objects
      // Create a mapping from goal ID to loaded image
      const imagePromises = moodboardFilteredData.map((item) => {
        if (!item.img) return Promise.resolve({ id: item.id, image: null });
        return new Promise((resolve) => {
          const img = new Image();
          img.crossOrigin = "Anonymous";
          img.onload = () => resolve({ id: item.id, image: img });
          img.onerror = () => {
            console.error(`Failed to load image: ${item.img}`);
            resolve({ id: item.id, image: null });
          };
          img.src = item.img;
        });
      });

      const loadedImagesArray = await Promise.all(imagePromises);

      // Create a map from goal ID to image for quick lookup
      const imageMap = new Map(
        loadedImagesArray.map(({ id, image }) => [id.toString(), image])
      );

      // Draw each image onto the canvas based on the layout
      layout.forEach((layoutItem) => {
        const goalId = layoutItem.i;
        const image = imageMap.get(goalId);

        if (!image) return;

        // Calculate the destination rectangle on the canvas in pixels
        const dX = layoutItem.x * cellWidth;
        const dY = layoutItem.y * cellHeight;
        const dWidth = layoutItem.w * cellWidth;
        const dHeight = layoutItem.h * cellHeight;

        // Implement 'object-fit: cover' logic to crop the image
        const imageAspectRatio = image.naturalWidth / image.naturalHeight;
        const cellAspectRatio = dWidth / dHeight;

        let sX = 0,
          sY = 0,
          sWidth = image.naturalWidth,
          sHeight = image.naturalHeight;

        if (imageAspectRatio > cellAspectRatio) {
          // Image is wider than the cell, so crop sides
          sWidth = image.naturalHeight * cellAspectRatio;
          sX = (image.naturalWidth - sWidth) / 2;
        } else {
          // Image is taller than the cell, so crop top/bottom
          sHeight = image.naturalWidth / cellAspectRatio;
          sY = (image.naturalHeight - sHeight) / 2;
        }

        ctx.drawImage(image, sX, sY, sWidth, sHeight, dX, dY, dWidth, dHeight);
      });

      // Trigger the download with a filename that includes the aspect ratio
      const imageURL = canvas.toDataURL("image/png", 1.0);
      const link = document.createElement("a");
      link.href = imageURL;
      link.download = `vision_board_${selectedYear}_${
        selectedRatio === "video"
          ? "16-9"
          : selectedRatio === "landscape"
            ? "4-3"
            : selectedRatio === "square"
              ? "1-1"
              : "3-4"
      }.png`;
      link.click();
      link.remove();

      setIsRendering(false);
      setToast({
        message: copy.vision_board.download.success,
        type: "success",
      });
    } catch (error) {
      console.error("Error creating canvas:", error);
      setToast({ message: copy.vision_board.download.error, type: "error" });
      setIsRendering(false);
    }
  };

  const handleRatioChange = (ratio) => {
    setSelectedRatio(ratio);
  };

  return (
    <PageContentContainer>
      {toast.message && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast({ message: "", type: "" })}
        />
      )}

      {moodboardFilteredData.length > 0 ? (
        <>
          <Headline
            headline={copy.vision_board.headline}
            description={copy.vision_board.description}
            buttonCTA={copy.vision_board.download.button}
            onClickLink={handleDownloadImage}
            type="button"
            disabled={isRendering}
          />
          <div className="flex gap-2 mb-4">
            <p className="items-center flex">
              {copy.vision_board.aspect_ratio.label}
            </p>
            <button
              onClick={() => handleRatioChange("video")}
              className={`px-3 py-1 rounded-md card-base-border ${
                selectedRatio === "video"
                  ? "bg-accent-light"
                  : "bg-white cursor-pointer"
              }`}
            >
              {copy.vision_board.aspect_ratio.video}
            </button>
            <button
              onClick={() => handleRatioChange("landscape")}
              className={`px-3 py-1 rounded-md card-base-border ${
                selectedRatio === "landscape"
                  ? "bg-accent-light"
                  : "bg-white cursor-pointer"
              }`}
            >
              {copy.vision_board.aspect_ratio.landscape}
            </button>
            <button
              onClick={() => handleRatioChange("square")}
              className={`px-3 py-1 rounded-md card-base-border ${
                selectedRatio === "square"
                  ? "bg-accent-light"
                  : "bg-white cursor-pointer"
              }`}
            >
              {copy.vision_board.aspect_ratio.square}
            </button>
            <button
              onClick={() => handleRatioChange("portrait")}
              className={`px-3 py-1 rounded-md card-base-border ${
                selectedRatio === "portrait"
                  ? "bg-accent-light"
                  : "bg-white hover:bg-gray-200"
              }`}
            >
              {copy.vision_board.aspect_ratio.portrait}
            </button>
          </div>
          <div className="max-md:flex max-md:items-center max-md:flex-col max-md:flex-1">
            <div
              className={`card-base md:h-auto w-full md:max-w-full card-base-border overflow-hidden ${aspectRatios[selectedRatio]}`}
              ref={visionBoardRef}
            >
              <VisionBoard />
            </div>
          </div>
        </>
      ) : (
        <StartMessage
          title={copy.start_message.headline}
          message={copy.start_message.description}
        />
      )}
    </PageContentContainer>
  );
};

export default VisionBoardPage;
